import React from 'react';
import { cn } from '@/ui/lib/utils';
import { SavingsIcon } from '@/ui/icons';
import { delimited } from '@/common/utils/commonUtil';
import {
  type AngelTax,
  toDisplayAmount,
  toDisplayYen,
} from '../../models/questions/angelTaxCalculator';

// PC の横並びは中身がカード 852px 以上を要して md（768px）に収まらないため、このカードだけ lg で切り替える。
// 種別色は questions.scss の .angel-tax-card--{tone} が --angel-type に定義し、中の要素は tw:bg-(--angel-type)/10 のように参照する。
// 薄い面（/10・/20）と区切り線（/50）は不透明度で作る

interface Props {
  // 優遇措置の種別。種別色を決める
  tone: 'a' | 'b' | 'pre-seed';
  // 優遇措置A など
  title: string;
  // 控除: (投資額−2,000)をその年の総所得金額等から控除 など
  deductionNote: string;
  // 上限: 800万円と総所得金額×40%のいずれか低い方 など
  limitNote: string;
  // 株式譲渡時点の取得価額の扱い。A/B は課税繰延、プレシード・シード特例は非課税
  disposal: 'taxDeferral' | 'taxExempt';
  angelTax: AngelTax;
}

// 「① 投資時点」「② 株式譲渡時点」の見出し。SP ではブロック上部の帯、PC では左側の正方形のラベル
function BlockLabel({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-1 tw:rounded-t-md tw:px-3 tw:py-1 tw:text-sm tw:leading-[1.3] tw:font-semibold tw:tracking-text tw:text-primary-foreground',
        'tw:lg:w-27 tw:lg:shrink-0 tw:lg:flex-col tw:lg:gap-0 tw:lg:self-stretch tw:lg:rounded-none tw:lg:text-center',
        className,
      )}
    >
      {children}
    </div>
  );
}

// 課税繰延 / 非課税 / 損失発生 のタグ。幅は固定（SP 72px / PC 60px）、高さは隣の説明文の行に合わせて伸びる
function Tag({ className, children }: { className: string; children: string }) {
  return (
    <span
      className={cn(
        'tw:flex tw:w-18 tw:shrink-0 tw:items-center tw:justify-center tw:self-stretch tw:rounded-md tw:px-2 tw:py-1 tw:text-xs tw:leading-none tw:font-semibold tw:tracking-text tw:whitespace-nowrap tw:lg:w-15',
        className,
      )}
    >
      {children}
    </span>
  );
}

export default function AngelTaxResultCard({
  tone,
  title,
  deductionNote,
  limitNote,
  disposal,
  angelTax,
}: Props) {
  const refund = toDisplayAmount(angelTax.refund);

  return (
    <section
      className={cn(
        'tw:flex tw:flex-col tw:overflow-hidden tw:rounded-lg tw:border tw:border-(--angel-type) tw:bg-background tw:lg:flex-row',
        `angel-tax-card--${tone}`,
      )}
    >
      {/* 優遇措置の名前。SP は上部の帯、PC は左の縦長パネル */}
      <h3 className="tw:m-0 tw:flex tw:items-center tw:justify-center tw:p-3 tw:text-center tw:font-noto tw:text-xl tw:leading-[1.3] tw:font-bold tw:text-primary-foreground tw:lg:w-43 tw:lg:shrink-0 tw:lg:text-2xl tw:bg-(--angel-type)">
        {title}
      </h3>

      <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:px-5 tw:py-4 tw:lg:flex-row tw:lg:items-center tw:lg:gap-4 tw:lg:px-6 tw:lg:py-5">
        {/* 控除額と適用時の所得税額 */}
        <dl className="tw:m-0 tw:flex tw:flex-col tw:gap-3 tw:px-3 tw:lg:w-53 tw:lg:shrink-0 tw:lg:px-0">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <dt className="tw:text-sm tw:leading-[1.7]">
              総所得から控除できる額
            </dt>
            <dd className="tw:m-0 tw:flex tw:h-9 tw:items-center tw:justify-end tw:bg-muted tw:px-3 tw:text-base tw:font-semibold tw:tracking-text tw:lg:h-12">
              {toDisplayYen(angelTax.deduction)}
            </dd>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1 tw:lg:border-t tw:lg:border-border tw:lg:pt-3">
            <dt className="tw:text-sm tw:leading-[1.7]">
              エンジェル税制適用時の所得税額
            </dt>
            <dd className="tw:m-0 tw:flex tw:h-9 tw:items-center tw:justify-end tw:bg-muted tw:px-3 tw:text-base tw:font-semibold tw:tracking-text tw:lg:h-12">
              {toDisplayYen(angelTax.incomeTax)}
            </dd>
          </div>
        </dl>

        {/* 矢印（Figma の Polygon: 種別色を 30% にした三角形）。SP では下向き、PC では右向き */}
        <svg
          aria-hidden
          width="40"
          height="14"
          viewBox="0 0 40 13.3333"
          className="tw:mx-auto tw:shrink-0 tw:lg:hidden tw:text-(--angel-type)"
        >
          <path
            d="M20 13.3333L0 0L40 0L20 13.3333Z"
            fill="currentColor"
            fillOpacity="0.3"
          />
        </svg>
        <svg
          aria-hidden
          width="24"
          height="61"
          viewBox="0 0 24 61"
          className="tw:hidden tw:shrink-0 tw:lg:block tw:text-(--angel-type)"
        >
          <path
            d="M24 30.5L4 61L4 0L24 30.5Z"
            fill="currentColor"
            fillOpacity="0.3"
          />
        </svg>

        {/* 節税効果。PC では全体を淡色の面で包み、SP ではブロックごとに面を持つ */}
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:rounded-md tw:lg:gap-4 tw:lg:p-5 tw:lg:bg-(--angel-type)/10">
          {/* ① 投資時点 */}
          <div
            // PC の高さは Figma の 141px（内容より高いので右カラムを上下中央に置く）
            className="tw:flex tw:flex-col tw:rounded-md tw:bg-(--angel-type)/10 tw:lg:min-h-35 tw:lg:flex-row tw:lg:gap-3 tw:lg:rounded-none tw:lg:bg-transparent"
          >
            <BlockLabel className="tw:bg-(--angel-type)">
              <span>①</span>
              <span>投資時点</span>
            </BlockLabel>
            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:p-3 tw:lg:justify-center tw:lg:p-0">
              {/* ラベルと金額は 8px、その塊と注記の間は 12px（Figma の入れ子と同じ） */}
              <div className="tw:flex tw:flex-col tw:gap-2">
                <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:leading-[1.4] tw:font-semibold tw:tracking-text tw:lg:text-base tw:text-(--angel-type)">
                  <SavingsIcon className="tw:size-4 tw:lg:size-5" />
                  投資節税効果
                </div>
                <p className="tw:m-0 tw:flex tw:items-end tw:gap-1">
                  <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:justify-end tw:bg-background tw:px-2 tw:py-1.5 tw:font-jost tw:text-4xl tw:leading-none tw:font-semibold tw:tracking-[-0.02em] tw:lg:px-3 tw:lg:py-1 tw:lg:text-5xl">
                    {delimited(refund)}
                  </span>
                  <span className="tw:w-5 tw:pb-2 tw:text-center tw:text-xl tw:leading-none tw:font-semibold tw:lg:pb-1 tw:text-(--angel-type)">
                    円
                  </span>
                </p>
              </div>
              <dl className="tw:m-0 tw:flex tw:flex-col tw:gap-1 tw:text-(length:--angel-tax-text-note) tw:leading-[1.4]">
                <div className="tw:flex tw:gap-3">
                  <dt className="tw:shrink-0 tw:font-semibold">控除</dt>
                  <dd className="tw:m-0">{deductionNote}</dd>
                </div>
                <div className="tw:flex tw:gap-3">
                  <dt className="tw:shrink-0 tw:font-semibold">上限</dt>
                  <dd className="tw:m-0">{limitNote}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div
            aria-hidden
            className="tw:hidden tw:border-t tw:lg:block tw:border-(--angel-type)/50"
          />

          {/* ② 株式譲渡時点 */}
          <div className="tw:flex tw:flex-col tw:rounded-md tw:lg:min-h-25 tw:lg:flex-row tw:lg:gap-3 tw:lg:rounded-none tw:lg:bg-transparent tw:bg-(--angel-type)/10">
            <BlockLabel className="tw:bg-(--angel-type)">
              <span>②</span>
              <span>
                株式譲渡
                <br className="tw:hidden tw:lg:inline" />
                時点
              </span>
            </BlockLabel>
            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2 tw:p-3 tw:lg:justify-center tw:lg:p-0">
              <div className="tw:flex tw:items-stretch tw:gap-2">
                {disposal === 'taxExempt' ? (
                  <>
                    {/* SP では Figma がタグの高さを 48px に揃え、説明文をその中央に置く（PC は文の高さなり） */}
                    <Tag className="tw:bg-(--angel-type)/20 tw:text-(--angel-type) tw:min-h-12 tw:lg:min-h-0">
                      非課税
                    </Tag>
                    <p className="tw:m-0 tw:self-center tw:text-(length:--angel-tax-text-note) tw:leading-[1.4]">
                      取得価額の調整なし
                      <br />
                      (年間20億円まで。
                      <br className="tw:lg:hidden" />
                      超える分は課税繰延)
                    </p>
                  </>
                ) : (
                  <>
                    <Tag className="tw:bg-(--angel-type)/20 tw:text-(--angel-type)">
                      課税繰延
                    </Tag>
                    <p className="tw:m-0 tw:flex tw:flex-col tw:text-(length:--angel-tax-text-note) tw:leading-[1.4]">
                      上記の控除額分だけ
                      <br className="tw:lg:hidden" />
                      株式取得価額が減額
                      {/* Figma の Polygon（種別色のベタ）。左端から 12px の位置に置く */}
                      <svg
                        aria-hidden
                        width="26"
                        height="8"
                        viewBox="0 0 26 8"
                        className="tw:my-0.75 tw:shrink-0 tw:text-(--angel-type)"
                      >
                        <path d="M19 8L12 0L26 0L19 8Z" fill="currentColor" />
                      </svg>
                      その分だけ課税対象額が増加
                    </p>
                  </>
                )}
              </div>
              <div className="tw:flex tw:items-stretch tw:gap-2">
                <Tag className="tw:bg-border tw:text-muted-foreground">
                  損失発生
                </Tag>
                <p className="tw:m-0 tw:flex tw:flex-col tw:gap-px tw:text-(length:--angel-tax-text-note) tw:leading-[1.4]">
                  その年の他の株式譲渡益と通算
                  <span className="tw:text-2xs">
                    翌年以降3年にわたり可能 ※破産・解散等した場合も可
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
